import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useUserContext } from 'providers/UserProvider';

import Table from 'components/common/Table';
import PrintSizeDropdown from 'components/common/PrintSizeDropdown';

import { putAwayApi } from 'api';

import {
  getPutAwayListColumns,
  PUT_AWAY_FILTER_OPTIONS,
} from 'constants/putAway';

import paths from 'routes/paths';
import {
  printPutAwayInstructionPdf,
  getPutAwayInstructionErrorMessage,
} from 'utils/putAwayInstructionPrint';
import { PRINT_PAGE_SIZE_STORAGE_KEYS } from 'constants/printPageSize';

const Putaway = () => {
  const navigate = useNavigate();

  const { selectedTenant, selectedWarehouse } = useUserContext();

  const tenant = selectedTenant?.domains?.[0]?.domain;
  const warehouse = selectedWarehouse?.domain;
  const initialFilter = { warehouse: selectedWarehouse?.id };

  const headerActions = (
    <Button
      variant="primary"
      size="sm"
      onClick={() => navigate(paths.putAway.create(tenant, warehouse))}
    >
      Create Putaway
    </Button>
  );

  const exportApi = useCallback(params => {
    return putAwayApi.exportPutaway(params);
  }, []);

  const handlePrintInstruction = useCallback(
    async (putawayId, pageSize) => {
      try {
        await printPutAwayInstructionPdf(putawayId, pageSize, {
          tenantName: selectedTenant?.name,
          warehouseName: selectedWarehouse?.name,
        });
      } catch (error) {
        toast.error(getPutAwayInstructionErrorMessage(error));
      }
    },
    [selectedTenant?.name, selectedWarehouse?.name],
  );

  const columns = useMemo(() => {
    // Instruction is only available once the putaway has been completed
    const printColumn = {
      accessorKey: 'print_instruction',
      header: 'Instruction',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-center' },
        cellProps: { className: 'text-center' },
      },
      cell: ({ row: { original } }) => {
        const { id, completed_at } = original;
        if (!completed_at) return null;

        return (
          <PrintSizeDropdown
            storageKey={PRINT_PAGE_SIZE_STORAGE_KEYS.PUT_AWAY_INSTRUCTION}
            title="Print put-away instruction"
            variant="link"
            className="p-0"
            onPrint={pageSize => handlePrintInstruction(id, pageSize)}
          />
        );
      },
    };

    return [...getPutAwayListColumns(tenant, warehouse), printColumn];
  }, [tenant, warehouse, handlePrintInstruction]);

  return (
    <Table
      title="Putaway"
      apiService={putAwayApi}
      exportApi={exportApi}
      sortable={true}
      columns={columns}
      filterOptions={PUT_AWAY_FILTER_OPTIONS}
      initialFilter={initialFilter}
      headerActions={headerActions}
    />
  );
};

export default Putaway;
